import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { settingsApi } from "@/api/settings";
import { useAuthStore } from "@/auth/store";
import { authKeys } from "@/hooks/useAuth";
import type { User } from "@/types/auth";
import type {
  ChangePasswordPayload,
  NotificationPreferences,
  PersonalizationPreferences,
  PersonalizationUpdate,
  UpdateProfilePayload,
} from "@/types/settings";

export const settingsKeys = {
  all: ["settings"] as const,
  profile: () => [...settingsKeys.all, "profile"] as const,
  sessions: () => [...settingsKeys.all, "sessions"] as const,
  trustedDevices: () => [...settingsKeys.all, "trusted-devices"] as const,
  notifications: () => [...settingsKeys.all, "notifications"] as const,
  personalization: () => [...settingsKeys.all, "personalization"] as const,
};

// --- Profile ---

export function profileQueryOptions() {
  return queryOptions({
    queryKey: settingsKeys.profile(),
    queryFn: () => settingsApi.getProfile(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useProfile() {
  return useQuery(profileQueryOptions());
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateProfilePayload) =>
      settingsApi.updateProfile(payload),
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(settingsKeys.profile(), updatedProfile);
      const currentUser = queryClient.getQueryData<User>(authKeys.me());
      if (currentUser) {
        queryClient.setQueryData<User>(authKeys.me(), {
          ...currentUser,
          id: updatedProfile.id,
          email: updatedProfile.email,
          firstName: updatedProfile.firstName,
          lastName: updatedProfile.lastName,
        });
      }
    },
  });
}

export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: FormData) => settingsApi.uploadAvatar(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.profile() });
    },
  });
}

export function useDeleteAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => settingsApi.deleteAvatar(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.profile() });
    },
  });
}

// --- Security ---

export function useChangePassword() {
  return useMutation({
    mutationFn: (payload: ChangePasswordPayload) =>
      settingsApi.changePassword(payload),
  });
}

export function useSessions() {
  return useQuery({
    queryKey: settingsKeys.sessions(),
    queryFn: () => settingsApi.getSessions(),
    staleTime: 60 * 1000,
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => settingsApi.revokeSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.sessions() });
    },
  });
}

export function useTrustedDevices() {
  return useQuery({
    queryKey: settingsKeys.trustedDevices(),
    queryFn: () => settingsApi.getTrustedDevices(),
    staleTime: 60 * 1000,
  });
}

export function useRemoveTrustedDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deviceId: string) => settingsApi.removeTrustedDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: settingsKeys.trustedDevices(),
      });
    },
  });
}

export function useRevokeOtherSessions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => settingsApi.revokeOtherSessions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.sessions() });
    },
  });
}

// --- Notifications ---

export function useNotificationPreferences() {
  return useQuery({
    queryKey: settingsKeys.notifications(),
    queryFn: () => settingsApi.getNotificationPreferences(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Partial<NotificationPreferences>) =>
      settingsApi.updateNotificationPreferences(payload),
    onMutate: async (newPrefs) => {
      await queryClient.cancelQueries({
        queryKey: settingsKeys.notifications(),
      });
      const previous = queryClient.getQueryData<NotificationPreferences>(
        settingsKeys.notifications(),
      );
      queryClient.setQueryData<NotificationPreferences>(
        settingsKeys.notifications(),
        (old) => (old ? { ...old, ...newPrefs } : undefined),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          settingsKeys.notifications(),
          context.previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: settingsKeys.notifications(),
      });
    },
  });
}

// --- Personalization ---

export function usePersonalization() {
  return useQuery({
    queryKey: settingsKeys.personalization(),
    queryFn: () => settingsApi.getPersonalization(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdatePersonalization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PersonalizationUpdate) => {
      const current = queryClient.getQueryData<PersonalizationPreferences>(
        settingsKeys.personalization(),
      );
      return settingsApi.updatePersonalization({
        theme: payload.theme ?? current?.theme ?? "system",
        ...payload,
      });
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: settingsKeys.personalization(),
      });
      const previous = queryClient.getQueryData<PersonalizationPreferences>(
        settingsKeys.personalization(),
      );
      if (payload.theme && previous) {
        queryClient.setQueryData(settingsKeys.personalization(), {
          ...previous,
          theme: payload.theme,
        });
      }
      return { previous };
    },
    onSuccess: (preferences) => {
      queryClient.setQueryData(settingsKeys.personalization(), preferences);
    },
    onError: (_err, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          settingsKeys.personalization(),
          context.previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: settingsKeys.personalization(),
      });
    },
  });
}

// --- Privacy ---

export function useDownloadDataExport() {
  return useMutation({
    mutationFn: async () => {
      const { blob, response } = await settingsApi.downloadDataExport();
      const disposition = response.headers.get("Content-Disposition");
      const filename =
        disposition?.match(/filename="([^"]+)"/)?.[1] ??
        `adara-export-${new Date().toISOString().split("T")[0]}.json`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { clearAuth } = useAuthStore();

  return useMutation({
    mutationFn: (password: string) => settingsApi.deleteAccount(password),
    onSuccess: () => {
      clearAuth();
      queryClient.clear();
      void navigate({ to: "/login" });
    },
  });
}
